import { call, put, takeLatest } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  startImpersonation,
  stopImpersonation,
  impersonationStart,
  impersonationSuccess,
  impersonationFailure,
  impersonationCleared,
  IMPERSONATION_KEYS,
} from "../slices/impersonationSlice";
import { makeRequest } from "../../utils/api";
import { extractErrorMessage } from "../../utils/helpers";

interface StartResponse {
  access_token: string;
  expires_at: string;
  target_org_id: string;
  target_brand_name: string | null;
  target_user_email: string;
}

function* startImpersonationSaga(
  action: PayloadAction<{ targetOrgId: string; reason: string }>,
): Generator<any, void, any> {
  try {
    yield put(impersonationStart());
    const response: StartResponse = yield call(
      makeRequest,
      "post",
      "/impersonation/start",
      {
        target_org_id: action.payload.targetOrgId,
        reason: action.payload.reason,
      },
    );

    sessionStorage.setItem(IMPERSONATION_KEYS.token, response.access_token);
    sessionStorage.setItem(
      IMPERSONATION_KEYS.meta,
      JSON.stringify({
        targetOrgId: response.target_org_id,
        targetBrandName: response.target_brand_name,
        targetUserEmail: response.target_user_email,
        expiresAt: response.expires_at,
      }),
    );

    yield put(
      impersonationSuccess({
        targetOrgId: response.target_org_id,
        targetBrandName: response.target_brand_name,
        targetUserEmail: response.target_user_email,
        expiresAt: response.expires_at,
      }),
    );

    // Reload into the impersonated context — cheapest correct way to flush
    // every cached slice (profile, brands, dashboard, etc.).
    window.location.assign("/dashboard");
  } catch (error: any) {
    yield put(impersonationFailure(extractErrorMessage(error)));
    toast.error(extractErrorMessage(error));
  }
}

function* stopImpersonationSaga(): Generator<any, void, any> {
  try {
    // Best-effort revoke; ignore failures so the user can always exit.
    yield call(makeRequest, "post", "/impersonation/stop", {});
  } catch {
    // intentionally swallowed
  } finally {
    sessionStorage.removeItem(IMPERSONATION_KEYS.token);
    sessionStorage.removeItem(IMPERSONATION_KEYS.meta);
    yield put(impersonationCleared());
    window.location.assign("/dashboard");
  }
}

export default function* impersonationSaga(): Generator<any, void, any> {
  yield takeLatest(startImpersonation.type, startImpersonationSaga);
  yield takeLatest(stopImpersonation.type, stopImpersonationSaga);
}
